/* The card kit (ui-map-interface.md §8 "The card family"; mockup round 083
   cards 06 to 09, round 084 cards 01 to 09, round 087 card 03). The markup
   is `ui/cards/card.ts`.

   One card in three sizes: the peek (a hover or a long press), the card
   (the selection) and the page (the card at the full width of its host).
   The card fills the width of its host; the host sets the width. Every
   rule reads the system tokens of `gen/tokens.css` (§2.3) and the inks by
   role of §2.4: a value in the text ink, a key and a label in the cap
   style and the dim ink, a control in the accent, a map object's name in
   its owner's faction colour, a catalogue item's name in the hero ink.
   `--faint` inks an off state, a rule, a track and a hatch alone.

   Every rule stands under `#ui .ck`, because `#ui button` sets the face of
   every chrome button and an id outranks a class. */

@import '../../gen/tokens.css';

#ui .ck {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  background: var(--g-card);
  border: 1px solid var(--edge);
  border-radius: var(--r-card);
  color: var(--text);
  font: var(--t-body);
  overflow: hidden;
  pointer-events: auto;
}
/* The peek ground: the panel ink with the blur, because a peek stands over
   the map for a moment (§4.2 of the review). */
#ui .ck.ck-peekg {
  background: var(--g-peek);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
}
/* The faction edge (R1875): the left side of the border of a peek and a
   card takes the ink of the owner of the thing it names, 2 px wide as the
   tooltip had it before the card family. The builder writes the ink as
   `--ck-own` on a card whose thing has an owner; any other card keeps the
   plain edge. */
#ui .ck.ck-own { border-left: 2px solid var(--ck-own); }
/* The page is the card at the full width of its host, with no frame. */
#ui .ck.ck-sz-page {
  border-radius: 0;
  border-width: 1px 0 0;
  background: transparent;
}

/* --- the head: 40 px, the mark, the title, the kind, a chip, the ✕ ---
   The last thing of the head (the status chip of a peek, the ✕ of a card)
   stands as far from the right edge as a press of the action row stands
   from its side: one token, `--s-4` (R1845). The head keeps its 40 px, so
   the top distance does not change. */
#ui .ck .ck-head {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  min-height: 40px;
  padding: var(--s-2) var(--s-4);
}
#ui .ck .ck-mk { display: inline-grid; place-items: center; width: 20px; height: 20px; flex: none; }
#ui .ck .ck-mark { display: block; }
#ui .ck .ck-ht { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: var(--s-1); }
#ui .ck .ck-tl > b {
  font: var(--t-title);
  letter-spacing: var(--t-title-track);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The title ink of a thing with no faction (§2.4): a catalogue item in the
   hero ink, nobody's thing in the text ink, and the side no player
   commands in the NPC ink with its dark edge (round 088 card 03 a). */
#ui .ck .ck-tl > b.i-cat { color: var(--hero-ink); }
#ui .ck .ck-tl > b.i-neu { color: var(--text); }
#ui .ck .ck-tl > b.i-npc {
  color: var(--npc);
  text-shadow: 0 0 1px var(--npc-edge), 0 0 2px var(--npc-edge);
}
#ui .ck .ck-ht > i,
#ui .ck .ck-kl > i {
  font: var(--t-cap);
  font-style: normal;
  letter-spacing: var(--t-cap-track);
  text-transform: uppercase;
  color: var(--dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* The chart position of a peek (R2742): the figures of the graticule in
   the step of the kind line, as written and in tabular figures. */
#ui .ck .ck-ht > i.ck-pos {
  text-transform: none;
  font-variant-numeric: tabular-nums;
}
/* A group digit or a REPORT mark after the title stays on the title line,
   and the name gives up its width first. */
#ui .ck .ck-tl { display: flex; align-items: center; gap: var(--s-2); min-width: 0; }
#ui .ck .ck-tl > b { flex: 0 1 auto; min-width: 0; }
#ui .ck .ck-tl > :not(b) { flex: none; }
/* The kind line with the status chip at its end (R1879): a strategic
   object's chip WINDOW hh:00 stands under the title, so the title keeps
   the whole width of the head. The kind gives up its width first. */
#ui .ck .ck-kl { display: flex; align-items: center; gap: var(--s-2); min-width: 0; }
#ui .ck .ck-kl > i { flex: 0 1 auto; min-width: 0; }

/* The one ✕: 24 px painted, 44 px to a finger (083 card 08). */
#ui .ck .ck-x {
  position: relative;
  flex: none;
  width: 24px;
  height: 24px;
  min-height: 0;
  margin-left: var(--s-2);
  padding: 0;
  display: grid;
  place-items: center;
  border: 1px solid rgb(var(--text-rgb) / 0.22);
  border-radius: var(--r-button);
  background: transparent;
  color: var(--text);
  font: var(--t-small);
  letter-spacing: 0;
}
#ui .ck .ck-x::after { content: ""; position: absolute; inset: -10px; }

/* --- the chips --- */
#ui .ck .ck-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--s-2);
  height: 20px;
  padding: 0 6px;
  flex: none;
  border: 1px solid rgb(var(--text-rgb) / 0.25);
  border-radius: var(--r-button);
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  text-transform: uppercase;
  color: var(--dim);
  white-space: nowrap;
}
#ui .ck .ck-chip.good { color: var(--good); border-color: rgb(var(--good-rgb) / 0.45); }
#ui .ck .ck-chip.bad { color: var(--bad); border-color: rgb(var(--bad-rgb) / 0.5); }
#ui .ck .ck-chip.amber { color: var(--amber); border-color: rgb(var(--amber-rgb) / 0.5); }
#ui .ck .ck-chip.on {
  color: var(--accent);
  border-color: rgb(var(--accent-rgb) / 0.5);
  background: rgb(var(--accent-rgb) / 0.1);
}
#ui .ck .ck-chip.solid { background: var(--hero-ink); border-color: var(--hero-ink); color: rgb(var(--deep-rgb)); }
#ui .ck .ck-chip.kind { border-style: dashed; }
/* The armed order (R2164): the order chip on the accent ground, so the
   armed state reads by fill as well as by its words, PATROL · PICK. */
#ui .ck .ck-chip.pick { background: var(--accent); border-color: var(--accent); color: rgb(var(--deep-rgb)); }
/* The armed line under the head (R2164): what the next map press does, in
   the text ink at the small step, and CANCEL at its right. An accent rule
   over it ties it to the chip. */
#ui .ck .ck-arm {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  margin: 0 var(--s-4);
  padding: var(--s-2) 0;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.35);
}
#ui .ck .ck-arm > p { flex: 1; min-width: 0; margin: 0; font: var(--t-small); color: var(--text); }
#ui .ck .ck-arm > .ck-b { flex: none; }
/* The group digit after the name (R1845): the look of the group badge of
   the map, the owner's faction colour (set by `groupChipHtml`) on the dark
   ground with an outline of the same ink, one step clear of the name. */
#ui .ck .ck-grp {
  display: inline-grid;
  place-items: center;
  min-width: var(--s-5);
  height: var(--s-5);
  box-sizing: border-box;
  margin-left: var(--s-2);
  padding: 0 var(--s-2);
  border: 1px solid color-mix(in srgb, currentColor 55%, transparent);
  border-radius: var(--r-bar);
  background: rgb(var(--deep-rgb) / 0.88);
  font: var(--t-cap);
  letter-spacing: 0;
  vertical-align: middle;
}

/* --- the vitals: one row of up to four gauges (083 card 07) --- */
#ui .ck .ck-vitals {
  display: grid;
  gap: var(--s-4);
  padding: var(--s-2) var(--s-4) var(--s-3);
}
#ui .ck .ck-vitals.n1 { grid-template-columns: minmax(0, 1fr); }
#ui .ck .ck-vitals.n2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
#ui .ck .ck-vitals.n3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
#ui .ck .ck-vitals.n4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }
#ui .ck .ck-vt { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
#ui .ck .ck-vl {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--s-2);
  white-space: nowrap;
}
/* Three or four vitals stack the label over the figure, so four fit in
   the card width (084 card 09 risks). */
#ui .ck .ck-vitals.n3 .ck-vl,
#ui .ck .ck-vitals.n4 .ck-vl { flex-direction: column; align-items: flex-start; gap: 1px; }
#ui .ck .ck-cap {
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  text-transform: uppercase;
  color: var(--dim);
}
#ui .ck .ck-vf { font: var(--t-body); font-weight: 500; color: var(--text); font-variant-numeric: tabular-nums; }
#ui .ck .ck-vf small { font: var(--t-small); color: var(--dim); }
/* The count of a running wait right after the label word (R1853): the
   shield regen (R1503) or the tube reload, in the cap step with tabular
   figures, in a fixed slot of four characters that never wraps, so the
   label row keeps one line and one width while the count runs. */
#ui .ck .ck-vh { display: inline-flex; align-items: baseline; white-space: nowrap; }
#ui .ck .ck-vw {
  display: inline-block;
  min-width: 4ch;
  margin-left: var(--s-1);
  font: var(--t-cap);
  font-style: normal;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  text-transform: none;
  white-space: nowrap;
  color: var(--text);
}
#ui .ck .ck-bar {
  display: block;
  position: relative;
  height: 4px;
  border-radius: var(--r-bar);
  background: rgb(var(--text-rgb) / 0.14);
  overflow: hidden;
}
#ui .ck .ck-bar > b {
  display: block;
  height: 100%;
  border-radius: var(--r-bar);
  background: var(--hero-ink);
}
#ui .ck .ck-bar.i-shield > b { background: var(--shield); }
#ui .ck .ck-bar.i-ready > b { background: var(--accent); }
#ui .ck .ck-bar.i-gauge > b,
#ui .ck .ck-bar.i-store > b { background: var(--text); }
#ui .ck .ck-bar.low > b { background: var(--bad); }
/* The part that loads now (R1853): the next tube of a reload, a lighter
   segment of the fill's own ink after the fill. */
#ui .ck .ck-bar > em {
  position: absolute;
  top: 0;
  bottom: 0;
  background: var(--hero-ink);
  opacity: 0.4;
}
/* An unknown value: a hatched track and "?", never a blank (083 card 07). */
#ui .ck .ck-bar.unk {
  background: repeating-linear-gradient(135deg,
    rgb(var(--text-rgb) / 0.35) 0 2px, rgb(var(--text-rgb) / 0.08) 2px 5px);
}
/* The readiness floor, a 1 px tick (R769). */
#ui .ck .ck-bar > u {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: var(--readout-white);
  opacity: 0.7;
}

/* --- the readout chip of a guard (R2080, round 099 card 04) ---
   The pie of the clock, the cap word, the bad condition of the rounds in
   reach while the clock runs, and the wait. The pie fills clockwise from
   the top as the clock runs out; a ready guard shows the whole disc. */
#ui .ck .ck-guard {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  padding: 0 var(--s-4) var(--s-3);
  white-space: nowrap;
}
#ui .ck .ck-guard > .ck-cap { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
#ui .ck .ck-pie {
  flex: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 1px solid rgb(var(--text-rgb) / 0.5);
  background: conic-gradient(var(--accent) calc(var(--d, 0) * 360deg), rgb(var(--text-rgb) / 0.14) 0);
}
#ui .ck .ck-pie.on { background: var(--accent); }

/* --- the conditions row (v2 slot 1, round 088 card 02 a) --- */
#ui .ck .ck-conds {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s-2);
  padding: 0 var(--s-4) var(--s-3);
}
#ui .ck .ck-cond {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 22px;
  padding: 0 7px 0 6px;
  border: 1px dashed rgb(var(--faint-rgb) / 0.6);
  border-radius: var(--r-button);
  background: rgb(var(--deep-rgb) / 0.5);
  font: var(--t-small);
  color: var(--text);
  white-space: nowrap;
}
#ui .ck .ck-cond > i { font-style: normal; }
#ui .ck .ck-cond > b { font: var(--t-cap); letter-spacing: var(--t-cap-track); }
#ui .ck .ck-cond > em { font-style: normal; color: var(--dim); font-variant-numeric: tabular-nums; }
/* A tone is an ink AND a shape: a bad chip has a solid heavy edge, a good
   chip a solid edge, and a neutral chip the dashed edge, so the row reads
   in greyscale. */
#ui .ck .ck-cond.bad { border: 2px solid rgb(var(--bad-rgb) / 0.6); }
#ui .ck .ck-cond.bad > i, #ui .ck .ck-cond.bad > b { color: var(--bad); }
#ui .ck .ck-cond.good { border-style: solid; border-color: rgb(var(--good-rgb) / 0.45); }
#ui .ck .ck-cond.good > i, #ui .ck .ck-cond.good > b { color: var(--good); }

/* --- the facts: a cap key at the left, the value at the right --- */
#ui .ck .ck-facts { padding: 0 var(--s-4); }
#ui .ck .ck-fact {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--s-4);
  min-height: 24px;
  padding: 2px 0;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
}
/* A long value wraps onto more lines and never ends in an ellipsis: an
   unlock rung of the Signal Station is a whole sentence, and the German
   words run longer. A long key wraps inside 60% of the row. */
#ui .ck .ck-fact > .ck-cap { flex: none; max-width: 60%; }
#ui .ck .ck-fv {
  min-width: 0;
  text-align: right;
  overflow-wrap: break-word;
}
#ui .ck .ck-fv .fit { font-size: inherit; }
#ui .ck .ck-fv b.good { color: var(--good); }
/* The gain of a print beside a figure of the hull card (R1840). */
#ui .ck .ck-fv small.gain { font: var(--t-small); color: var(--good); }
#ui .ck .ck-fv b.gold { color: var(--amber); }
#ui .ck .ck-fv b.bad { color: var(--bad); }
@media (pointer: coarse) {
  #ui .ck .ck-fact { min-height: 32px; }
}

/* --- the effect block: NOW → AFTER and the change --- */
#ui .ck .ck-eff {
  display: grid;
  grid-template-columns: 1fr auto 12px auto 44px;
  column-gap: var(--s-3);
  align-items: center;
  margin: var(--s-2) var(--s-4) 0;
}
#ui .ck .ck-eff > * {
  min-height: 24px;
  display: flex;
  align-items: center;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
}
#ui .ck .ck-eff > .ck-eh { min-height: 20px; border-top: 0; justify-content: flex-end; }
#ui .ck .ck-eff .ck-en { color: var(--dim); justify-content: flex-end; }
#ui .ck .ck-eff .ck-ea { color: var(--faint); justify-content: center; }
#ui .ck .ck-eff .ck-ef { color: var(--hero-ink); justify-content: flex-end; }
#ui .ck .ck-eff .ck-ed { justify-content: flex-end; font: var(--t-small); }
#ui .ck .ck-eff .ck-ed.good { color: var(--good); }
#ui .ck .ck-eff .ck-ed.bad { color: var(--bad); }

/* --- the compare grid (084 card 17): the better figure takes ▲ --- */
#ui .ck .ck-cmp {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) repeat(var(--cols, 2), minmax(0, 1fr));
  padding: 0 var(--s-4);
}
#ui .ck .ck-cmp > span {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  min-height: 24px;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
  white-space: nowrap;
}
#ui .ck .ck-cmp > .ck-ch { border-top: 0; min-height: 22px; font: var(--t-cap); letter-spacing: var(--t-cap-track); color: var(--hero-ink); }
#ui .ck .ck-cmp > .ck-cv { justify-content: flex-end; }
#ui .ck .ck-cmp > .ck-cv.w { color: var(--hero-ink); font-weight: 500; }
#ui .ck .ck-cmp > .ck-cv.w::before {
  content: "";
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-bottom: 6px solid var(--hero-ink);
}
#ui .ck .ck-cmp > .ck-cv.l { color: var(--dim); }

/* --- a picker: the captain picker and the start sheet (R1893) ---
   The summary of a dive states three figures once, a head names the
   list, and the captain rows stand at the width of the card. */
#ui .ck .ck-sum {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--s-2);
  margin: var(--s-3) var(--s-4) 0;
}
#ui .ck .ck-sum > span {
  display: flex;
  flex-direction: column;
  padding: var(--s-2) var(--s-3);
  border-radius: var(--r-bar);
  background: rgb(var(--accent-rgb) / 0.05);
}
#ui .ck .ck-sum i { font-style: normal; font: var(--t-cap); letter-spacing: var(--t-cap-track); color: var(--dim); text-transform: uppercase; }
#ui .ck .ck-sum b { font: var(--t-body); font-weight: 500; color: var(--hero-ink); font-variant-numeric: tabular-nums; }
#ui .ck .ck-sum small { font: var(--t-small); color: var(--dim); }
#ui .ck h3.ck-ph { display: flex; gap: var(--s-2); margin: var(--s-3) var(--s-4) 0; }
#ui .ck h3.ck-ph i { margin-left: auto; font-style: normal; }
#ui .ck .ck-pick { display: flex; flex-direction: column; margin: var(--s-1) var(--s-2) 0; }
/* The LOADOUT sheet of a hull in the pens (R2031): one row for each round
   kind it loads, the name, the stepper of the choice kit, the MAX press »
   (R2040) and the stock of the hold. The stepper and MAX stand in the two
   middle columns, so the three rows line up. */
#ui .ck .ck-load { display: flex; flex-direction: column; gap: var(--s-2); padding: var(--s-3) var(--s-4) 0; }
#ui .ck .ck-ldrow {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto minmax(0, 1fr);
  align-items: center;
  gap: var(--s-3);
}
#ui .ck button.ck-ldmax { border: 1px solid var(--edge); border-radius: var(--r-button); }
#ui .ck .ck-ldname { font: var(--t-cap); letter-spacing: var(--t-cap-track); text-transform: uppercase; color: var(--text); }
/* The cargo of one item under the name, on the LOADOUT sheet of a Sapper (R2708). */
#ui .ck .ck-ldw { display: block; font: var(--t-small); letter-spacing: normal; text-transform: none; color: var(--dim); }
#ui .ck .ck-ldheld { font: var(--t-small); color: var(--dim); text-align: right; font-variant-numeric: tabular-nums; }

/* --- a choice (the round of a hold, a battle plan, the level picker) and
   a list body. A choice is a control of the choice kit (R2011) in its
   compact size; it takes the width of the section under the cap. --- */
#ui .ck .ck-sect { padding: var(--s-3) var(--s-4) 0; }
#ui .ck .ck-sect > :is(.ch-pg, .ch-ts) { display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); width: 100%; margin-top: var(--s-2); }
#ui .ck .ck-sect > :is(.ch-ol, .ch-sb) { margin-top: var(--s-2); }
/* The signature order of an own hull (R2376, R2405): the press with the
   order word, and beside it the charge over the recharge period, on one
   row. The press keeps its finger target and takes the width the state
   leaves. */
#ui .ck .ck-sigr { display: flex; align-items: center; gap: var(--s-3); margin-top: var(--s-2); }
#ui .ck .ck-sigr > .ck-b { flex: 1 1 auto; }
#ui .ck .ck-sigs { flex: none; max-width: 45%; display: flex; flex-direction: column; gap: 2px; font: var(--t-small); }
#ui .ck .ck-sigs > b { font-weight: inherit; color: var(--text); }
#ui .ck .ck-sigs > i { font-style: normal; color: var(--dim); }
#ui .ck .ck-list { padding: 0 var(--s-4); }
/* The STOW section of a Sapper card (v2-the-deep.md §11.1, round 088
   card 12 b, R2635): the head with the cargo, then one 44 px row for each
   kind aboard with its own LAY press. The row of the kind that LAY of the
   order row lays takes the accent ink on its name. The list of the
   section has no side padding of its own: the section holds it. */
#ui .ck .ck-stow > .ck-list { padding: 0; margin-top: var(--s-2); }
#ui .ck .ck-sr { cursor: default; }
#ui .ck .ck-sr > .bic { width: 18px; height: 18px; color: var(--dim); }
#ui .ck .ck-sr.on > .ck-rn > b { color: var(--accent); }
/* The back press of one sea object shown from the cell list (R2708). */
#ui .ck .ck-solback {
  display: block; width: 100%; min-height: 44px; margin: 0; padding: 0;
  border: 0; border-top: 1px solid rgb(var(--accent-rgb) / 0.09); background: transparent;
  color: var(--accent); font: var(--t-small); text-align: left; cursor: pointer;
}
/* The pager of the cell list (R2851): two 44 px presses round the page
   figure, under the rows of the page. */
#ui .ck .ck-clpager {
  display: flex; align-items: center; justify-content: space-between; gap: var(--s-2);
  border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
  font: var(--t-small); color: var(--dim); font-variant-numeric: tabular-nums;
}
#ui .ck .ck-clpg {
  min-width: 44px; min-height: 44px; margin: 0; padding: 0; border: 0; background: transparent;
  color: var(--accent); font: var(--t-body); cursor: pointer;
}
#ui .ck .ck-clpg:disabled { color: var(--dim); opacity: 0.4; cursor: default; }
/* The head over the sighted wrecks of the cell list: the reason the
   other wrecks fold. */
#ui .ck .ck-clhead {
  padding: var(--s-2) 0 var(--s-1); border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
  font: var(--t-small); color: var(--dim); letter-spacing: 0.08em; text-transform: uppercase;
}
/* The fold of the wrecks with no cargo sighting: one line, no press. */
#ui .ck .ck-clfold {
  padding: var(--s-2) 0; border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
  font: var(--t-small); color: var(--dim);
}
#ui .ck .ck-stown { margin: var(--s-2) 0 0; font: var(--t-small); color: var(--dim); }
#ui .ck .ck-row {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  gap: var(--s-3);
  align-items: center;
  width: 100%;
  min-height: 44px;
  margin: 0;
  padding: 0;
  border: 0;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
  border-radius: 0;
  background: transparent;
  color: var(--text);
  font: var(--t-body);
  text-align: left;
}
#ui .ck .ck-rn { display: flex; flex-direction: column; gap: var(--s-1); min-width: 0; }
#ui .ck .ck-rn > b { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
#ui .ck .ck-rn > i { font: var(--t-small); font-style: normal; color: var(--dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
#ui .ck .ck-rf { font-variant-numeric: tabular-nums; }
#ui .ck .ck-rf small { font: var(--t-small); color: var(--dim); }

/* --- the harbour plan, a section of the own hold card (round 086 card
   07 b, R1824) ---
   The section head names the plan and, at the right, the Harbour with its
   level. Each lane: the cap label and the figure, one berth square per
   point of the cap (filled in the hero ink up to the weight), and the
   hulls in the small step. The historical reading sets the section in the
   Fell steps (`ui/styles.css.tmpl`, `.ck-plan.hist`). */
#ui .ck .ck-sh {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--s-3);
}
#ui .ck .ck-shr { font: var(--t-small); color: var(--dim); white-space: nowrap; }
#ui .ck .ck-lane { margin-top: var(--s-2); }
#ui .ck .ck-lh { display: flex; align-items: baseline; justify-content: space-between; gap: var(--s-3); }
#ui .ck .ck-ll { font: var(--t-cap); letter-spacing: var(--t-cap-track); text-transform: uppercase; color: var(--text); }
#ui .ck .ck-lf { font: var(--t-small); color: var(--text); font-variant-numeric: tabular-nums; }
#ui .ck .ck-berths { display: flex; flex-wrap: wrap; gap: 2px; margin: var(--s-1) 0; }
#ui .ck .ck-berths > i {
  width: 6px;
  height: 6px;
  box-sizing: border-box;
  border: 1px solid rgb(var(--text-rgb) / 0.55);
  border-radius: 1px;
  background: rgb(var(--deep-rgb) / 0.6);
}
#ui .ck .ck-berths > i.on { border-color: var(--hero-ink); background: var(--hero-ink); }
#ui .ck .ck-lhulls { font: var(--t-small); color: var(--dim); }

/* --- the route profile of an own fleet card (round 088 card 05, R2616) ---
   The bands as rows in the deep ink at the alpha of the depth tints, the
   bed as a step line, the run deeper than the rating in the bad ink, the
   rating as a dashed line. The picture keeps the ratio of its view box. */
#ui .ck .ck-rp { display: block; width: 100%; height: auto; margin: var(--s-1) 0; }
#ui .ck .ck-rp .rp-band { fill: var(--c); stroke: none; }
#ui .ck .ck-rp path { fill: none; vector-effect: non-scaling-stroke; }
#ui .ck .ck-rp .rp-bed { stroke: var(--text); stroke-width: 1.3; stroke-linejoin: round; }
#ui .ck .ck-rp .rp-bad { stroke: var(--bad); stroke-width: 2.2; stroke-linejoin: round; }
#ui .ck .ck-rp .rp-rating { stroke: var(--text); stroke-width: 1.2; stroke-dasharray: 5 3; }
#ui .ck .ck-rp .rp-cross { stroke: var(--bad); stroke-width: 1; stroke-dasharray: 2 2; }
/* The naming sheet of a seamount (R2626, round 088 card 06): the two word
   pickers side by side, and the name they make under them. */
#ui .ck .ck-smw { display: flex; gap: var(--s-1); flex-wrap: wrap; }
#ui .ck .ck-smw .ch-sb { flex: 1 1 8em; min-width: 0; }
#ui .ck .ck-smn { margin: var(--s-1) 0 0; color: var(--text); }
/* The echo trace of the search hint (chart card 33, R2649): the line in
   the text ink before its word. */
#ui .ck .ck-echo { vertical-align: middle; margin-right: var(--s-1); color: var(--text); }
#ui .ck .ck-rfoot { display: flex; justify-content: space-between; gap: var(--s-2); font: var(--t-small); color: var(--dim); }
#ui .ck .ck-rfoot .bad { color: var(--bad); }

/* --- the ramp of the heat cell peek (round 086 card 12, R1825) ---
   The ramp of the mode that paints, with a marker at the cell's share,
   and the low end word, the share and the high end word under it. The
   peek words keep the chrome face in both readings: the heat field is a
   map filter and not chart furniture (086 open point 2). */
#ui .ck .ck-ramp {
  position: relative;
  display: block;
  height: 8px;
  border: 1px solid rgb(var(--text-rgb) / 0.25);
  border-radius: var(--r-bar);
}
#ui .ck .ck-ramp > b {
  position: absolute;
  top: -4px;
  bottom: -4px;
  width: 2px;
  margin-left: -1px;
  background: var(--hero-ink);
}
#ui .ck .ck-ends {
  display: flex;
  justify-content: space-between;
  gap: var(--s-3);
  margin-top: var(--s-2);
  font: var(--t-small);
  color: var(--dim);
}
#ui .ck .ck-ends > :nth-child(2) { color: var(--text); }

/* --- the terrain body of a ridge peek (round 086 card 06 b) ---
   The elevation along the ridge, in the text ink of the peek. The words
   of the peek stay in the chrome face; the drawing follows the reading:
   the ring figures take the cap step in the modern drawing and the Fell
   face in the historical one, at the Fell note step that the drawing
   states (`ui/cards/terrain.ts`). */
#ui .ck .ck-terrain { display: block; color: var(--text); overflow: visible; }
#ui .ck .ck-terrain text { fill: currentColor; }
#ui .ck .ck-terrain:not(.hist) text { font: var(--t-cap); }
#ui .ck .ck-terrain.hist text { font-family: var(--serif); font-style: italic; }
#ui .ck .ck-terrain .ck-at { stroke: var(--hero-ink); stroke-width: 1; fill: none; }

/* --- the note: the reason of a lock or a hint --- */
#ui .ck .ck-note { margin: var(--s-3) var(--s-4) 0; font: var(--t-small); color: var(--dim); }
#ui .ck .ck-note > p { margin: 0; }
#ui .ck .ck-note > p + p { margin-top: var(--s-1); }
#ui .ck .ck-note.bad { color: var(--bad); }
/* A note whose sentence changes with the state (R2745, the Depot line of
   the Sapper LOADOUT sheet): every sentence stands in one grid cell and
   only the one that applies is visible, so the note keeps the height of
   the longest and nothing under it moves. `.hl` is the highlight ink of a
   state that asks for the player, with no change of size or weight. */
#ui .ck .ck-swap { display: grid; }
#ui .ck .ck-swap > span { grid-area: 1 / 1; }
#ui .ck .ck-swap > .off { visibility: hidden; }
#ui .ck .ck-swap > .hl { color: var(--amber); }
/* A foot note stands under the actions, whose padding gives the gap. */
#ui .ck .ck-acts + .ck-note.foot { margin-top: 0; }

/* --- the actions: the dark face, the quiet face and ⋯ (087 card 03) --- */
/* The side padding is the head's right padding too (R1845). */
#ui .ck .ck-acts {
  display: flex;
  gap: var(--s-2);
  padding: var(--s-4);
}
#ui .ck .ck-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--s-2);
  padding: var(--s-4) var(--s-4) 0;
}
/* A section head of the ⋯ grid (R1845): the cap word over the whole width,
   with a rule over every head but the first, so the orders, the group
   slots and the name read as three groups. */
#ui .ck .ck-grid > .ck-gh {
  grid-column: 1 / -1;
  padding-top: var(--s-2);
  border-top: 1px solid rgb(var(--faint-rgb) / 0.6);
}
#ui .ck .ck-grid > .ck-gh:first-child { padding-top: 0; border-top: 0; }
/* The choice of a section of the EDIT grid (the group slots, R2011): it
   spans the grid, and its pills share the width. */
#ui .ck .ck-grid > .ck-gp { grid-column: 1 / -1; }
#ui .ck .ck-gp > .ch-pg { display: grid; width: 100%; }
#ui .ck > :last-child:not(.ck-acts) { margin-bottom: var(--s-4); }
#ui .ck > .ck-head:last-child { margin-bottom: 0; }
#ui .ck .ck-b {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--s-2);
  min-width: 0;
  min-height: var(--target-mouse);
  margin: 0;
  padding: 0 6px;
  flex: 1 1 0;
  border-radius: var(--r-card);
  color: var(--accent);
  font: var(--t-body);
  white-space: nowrap;
  cursor: pointer;
}
/* The word of a press never ends in an ellipsis (R2061): a verb or a
   reason that does not fit takes a second line, and a word longer than the
   press breaks. Only the free text of a label (a fleet name) can reach the
   end of the second line and lose its tail there. The order row is one
   row of three presses, and a long word of a second language (RÜCKKEHR in
   the HUNT slot, PATROUILLE as the action) still takes its second line. */
#ui .ck .ck-b > span {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  min-width: 0;
  overflow: hidden;
  white-space: normal;
  overflow-wrap: break-word;
  text-align: center;
  line-height: 1.2;
}
/* The dark face of LEAVE TO THE HUB (R1740): a ground darker than the
   card, a faint accent edge, accent words in the 500 weight and the wide
   tracking. The primary leads by its place (first), its width (1.6
   shares of the row), its edge and its weight, so the rank reads with no
   colour (087 card 03). */
#ui .ck .ck-b.dk {
  flex: 1.6 1 0;
  background: rgb(var(--deep-rgb) / 0.88);
  border: 1px solid rgb(var(--accent-rgb) / 0.34);
  font-weight: 500;
  letter-spacing: 0.14em;
}
/* The quiet face of a secondary: no ground, a fainter edge, the plain
   weight. */
#ui .ck .ck-b.qt {
  background: transparent;
  border: 1px solid rgb(var(--accent-rgb) / 0.2);
  letter-spacing: 0.04em;
}
/* An order that runs or a pick that waits: a lit plate with an accent bar
   at its foot. It is no solid light press (R1753). */
#ui .ck .ck-b.on {
  background: rgb(var(--accent-rgb) / 0.18);
  border-color: rgb(var(--accent-rgb) / 0.6);
  box-shadow: inset 0 -2px 0 var(--accent);
}
#ui .ck .ck-b:disabled {
  opacity: 1;
  cursor: not-allowed;
  color: var(--faint);
  border-color: rgb(var(--text-rgb) / 0.16);
}
#ui .ck .ck-b.dk:disabled { background: rgb(var(--deep-rgb) / 0.5); }
#ui .ck .ck-b.ck-more { flex: 0 0 var(--target-mouse); padding: 0; letter-spacing: 0.05em; }
/* EDIT in the action row, left of ⋯ (R2008): a quiet square press with the
   pencil in the 12 px box. */
#ui .ck .ck-b.ck-edit { flex: 0 0 var(--target-mouse); padding: 0; line-height: 1; }
@media (hover: hover) {
  #ui .ck .ck-b:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.14); color: var(--hover-ink); }
  #ui .ck .ck-x:hover { border-color: rgb(var(--text-rgb) / 0.5); color: var(--hover-ink); background: transparent; }
  #ui .ck .ck-row:hover { background: rgb(var(--accent-rgb) / 0.06); }
  #ui .ck .pp-sp:hover:not(:disabled),
  #ui .ck .pp-pick > button:hover:not(:disabled) { background: rgb(var(--accent-rgb) / 0.14); color: var(--hover-ink); }
}
/* The key cap at the right of the word, on a desktop alone. */
#ui .ck .ck-key {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  padding: 0 var(--s-2);
  box-sizing: border-box;
  border: 1px solid rgb(var(--accent-rgb) / 0.4);
  border-bottom-width: 2px;
  border-radius: var(--r-bar);
  font: var(--t-cap);
  letter-spacing: 0;
  line-height: 1;
  text-transform: uppercase;
}
/* A finger: every press takes the 44 px target, and no key cap shows. */
@media (pointer: coarse) {
  #ui .ck .ck-b { min-height: var(--target-finger); }
  #ui .ck .ck-b.ck-more { flex-basis: var(--target-finger); }
  #ui .ck .ck-b.ck-edit { flex-basis: var(--target-finger); }
  #ui .ck .ck-key { display: none; }
}

/* --- the build card of a catalogue item (R1826, round 084 cards 13 to 16) ---
   The item icon of the head, the sentence, a section head, the facts in
   two columns, the cost on the verb press, the tech ladder, the attribute
   vitals with the talent diamond, the career strip and the folded
   backstory. */
#ui .ck .ck-mk > .bic { width: 20px; height: 20px; color: var(--hero-ink); }
/* A hull card wears the hull mark of the class, as its shipyard row does
   (R1872): 20 px high, in the hero ink. */
#ui .ck .ck-mk > .ck-ship { width: 14px; height: 20px; color: var(--hero-ink); }
#ui .ck .ck-desc { margin: 0 var(--s-4) var(--s-2); font: var(--t-small); color: var(--dim); }
#ui .ck .ck-sh { margin: var(--s-3) var(--s-4) var(--s-1); }
#ui .ck .ck-facts.ck-f2 {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  column-gap: var(--s-5);
}
#ui .ck .ck-fv .done { color: var(--good); }
#ui .ck .ck-fv .locked { color: var(--faint); }
#ui .ck .ck-ht .term { font-size: inherit; letter-spacing: inherit; }
/* The cost on the verb press, after the verb, in the body step. */
#ui .ck .ck-b > .ck-bc {
  display: inline-flex;
  align-items: center;
  gap: var(--s-1);
  font-weight: 400;
  letter-spacing: 0;
  color: var(--text);
  overflow: hidden;
}
#ui .ck .ck-b:disabled > .ck-bc { color: var(--dim); }
/* The talent of a captain: a solid diamond, a shape and not an ink. */
#ui .ck .ck-tal {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-left: var(--s-2);
  background: var(--hero-ink);
  transform: rotate(45deg) translateY(-1px);
}
/* The ability rows of the captain card (round 100 card 01 "The next pip",
   R2087). Each attribute is a row: the name, ten pips to the cap and the
   practice percent; line 2 holds the season dots, the clock of an Academy
   job and the effect. A filled pip is a point in the text ink, the hero
   ink for a talent. The next pip fills from the left with the practice
   share (`--pf`), a plain fill; an Academy job stripes it in the accent,
   so the two read apart in greyscale. A prisoner's practice figures fade.
   Under the rows: one TRAIN press, or the pick of six chips with the
   Academy time of each. */
#ui .ck .ck-pp { padding: var(--s-1) var(--s-4) var(--s-2); }
#ui .ck .pp-r {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 34px;
  column-gap: 6px;
  align-items: center;
  padding: 4px 0 3px;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
}
#ui .ck .pp-r:first-child { border-top: 0; }
#ui .ck .pp-n { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font: var(--t-small); color: var(--text); }
#ui .ck .pp-r.t .pp-n { color: var(--hero-ink); }
#ui .ck .pp-star { margin-left: 3px; font: var(--t-cap); color: var(--hero-ink); }
#ui .ck .pp-pips { display: flex; gap: 2px; }
#ui .ck .pp-pips > i {
  width: 10px;
  height: 9px;
  box-sizing: border-box;
  border: 1px solid rgb(var(--text-rgb) / 0.3);
  border-radius: 1px;
}
#ui .ck .pp-pips > i.f { background: var(--text); border-color: var(--text); }
#ui .ck .pp-r.t .pp-pips > i.f { background: var(--hero-ink); border-color: var(--hero-ink); }
#ui .ck .pp-pips > i.p {
  border-color: rgb(var(--text-rgb) / 0.6);
  background: linear-gradient(90deg, rgb(var(--text-rgb) / 0.6) var(--pf), transparent var(--pf));
}
#ui .ck .pp-pips > i.a {
  border-color: var(--accent);
  background: repeating-linear-gradient(135deg, var(--accent) 0 2px, transparent 2px 4px);
}
#ui .ck .pp-pc { font: var(--t-small); font-variant-numeric: tabular-nums; color: var(--text); white-space: nowrap; text-align: right; }
#ui .ck .pp-pc.stop { color: var(--dim); font: var(--t-cap); letter-spacing: 0.06em; text-transform: uppercase; }
#ui .ck .pp-l2 { grid-column: 1 / -1; display: flex; align-items: center; gap: 8px; min-width: 0; margin-top: 2px; }
#ui .ck .pp-e { min-width: 0; margin-left: auto; font: var(--t-small); color: var(--dim); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
/* The three season dots: a filled dot is a practice point of this season.
   They stand only once one is filled (R2087). */
#ui .ck .pp-sd { display: inline-flex; gap: 2px; align-items: center; }
#ui .ck .pp-sd > i { width: 5px; height: 5px; box-sizing: border-box; border: 1px solid rgb(var(--text-rgb) / 0.5); border-radius: 50%; }
#ui .ck .pp-sd > i.on { background: var(--text); border-color: var(--text); }
#ui .ck .ck-pp.held .pp-pc, #ui .ck .ck-pp.held .pp-sd > i.on { opacity: 0.45; }
#ui .ck .pp-clk { font: var(--t-small); color: var(--accent); white-space: nowrap; font-variant-numeric: tabular-nums; }
#ui .ck .pp-clk::before { content: "\25F7 "; }
/* The foot: the TRAIN press with its one reason, or the line of a cadet
   and of a prisoner. */
#ui .ck .pp-foot { display: flex; align-items: center; gap: 8px; padding: var(--s-2) var(--s-4); }
#ui .ck .pp-why { margin: var(--s-1) var(--s-4) var(--s-2); font: var(--t-small); color: var(--dim); }
#ui .ck .pp-foot .pp-why { margin: 0; }
#ui .ck .pp-sp {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  min-height: 22px;
  margin: 0;
  padding: 1px 6px;
  border: 1px solid rgb(var(--accent-rgb) / 0.34);
  border-radius: var(--r-bar);
  background: rgb(var(--deep-rgb) / 0.88);
  color: var(--accent);
  font: var(--t-cap);
  letter-spacing: 0.08em;
  white-space: nowrap;
}
#ui .ck .pp-sp:disabled { border-color: rgb(var(--text-rgb) / 0.16); background: none; color: var(--faint); cursor: default; }
/* The pick of one Academy point: its head closes it, and six chips name
   the attribute, the step and the time of the next point. */
#ui .ck .pp-ph {
  display: flex;
  justify-content: space-between;
  width: calc(100% - 2 * var(--s-4));
  margin: var(--s-2) var(--s-4);
  padding: 0;
  background: none;
  border: 0;
  text-align: left;
  cursor: pointer;
}
#ui .ck .pp-ph > .ck-cap.on { color: var(--accent); }
#ui .ck .pp-pick { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--s-2); padding: 0 var(--s-4) var(--s-2); }
#ui .ck .pp-pick > button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  margin: 0;
  padding: 3px 2px;
  border: 1px solid rgb(var(--accent-rgb) / 0.3);
  border-radius: var(--r-button);
  background: rgb(var(--deep-rgb) / 0.88);
  color: var(--accent);
  font: var(--t-cap);
  letter-spacing: 0.06em;
}
#ui .ck .pp-pick > button small { font: var(--t-small); letter-spacing: 0; color: var(--text); font-variant-numeric: tabular-nums; }
#ui .ck .pp-pick > button:disabled { background: none; border-color: rgb(var(--text-rgb) / 0.16); color: var(--faint); cursor: default; }
#ui .ck .pp-pick > button:disabled small { color: var(--faint); }
/* A finger: larger pips, a taller row, and the 44 px target on every press. */
@media (pointer: coarse) {
  #ui .ck .pp-r { grid-template-columns: minmax(0, 1fr) auto 40px; padding: 6px 0 5px; }
  #ui .ck .pp-pips > i { width: 16px; height: 12px; }
  #ui .ck .pp-sp { min-width: var(--target-finger); min-height: var(--target-finger); }
  #ui .ck .pp-ph { min-height: var(--target-finger); align-items: center; }
  #ui .ck .pp-pick > button { min-height: var(--target-finger); }
}

/* The tech ladder (084 card 15): one row per step, the shape at the left.
   A check is done, a heavy ring is this node, a plain ring is a step still
   open on the path, a dashed ring with a lock comes later. */
#ui .ck .ck-lad { padding: 0 var(--s-4); }
#ui .ck .ck-lr {
  display: grid;
  grid-template-columns: 20px 28px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: var(--s-3);
  min-height: 28px;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.09);
}
#ui .ck .ck-ld {
  display: grid;
  place-items: center;
  width: 18px;
  height: 18px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 1px solid rgb(var(--text-rgb) / 0.35);
  color: var(--faint);
}
#ui .ck .ck-ld > .bic { width: 12px; height: 12px; }
#ui .ck .ck-lr.done .ck-ld { border-color: var(--good); color: var(--good); }
#ui .ck .ck-lr.this .ck-ld { border: 2px solid var(--hero-ink); }
#ui .ck .ck-lr.later .ck-ld { border-style: dashed; }
#ui .ck .ck-lr.this { background: rgb(var(--hero-ink-rgb) / 0.05); }
#ui .ck .ck-lr > b { font-weight: 400; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
#ui .ck .ck-lr.this > b { color: var(--hero-ink); font-weight: 500; }
#ui .ck .ck-lc { font: var(--t-cap); letter-spacing: 0.04em; color: var(--faint); }
#ui .ck .ck-lr > small { font: var(--t-small); color: var(--dim); white-space: nowrap; }

/* The career strip of a captain (084 card 16): rise, peak and decline, the
   mark at the age. The decline is hatched, so the three read in greyscale. */
#ui .ck .ck-car { padding: var(--s-1) var(--s-4) var(--s-3); }
#ui .ck .ck-cl {
  display: flex;
  justify-content: space-between;
  font: var(--t-cap);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--faint);
}
#ui .ck .ck-cs { position: relative; display: flex; gap: 2px; height: 8px; margin: 6px 0 4px; }
#ui .ck .ck-cs > i { display: block; height: 100%; border-radius: var(--r-bar); }
#ui .ck .ck-cs > i.rise { background: rgb(var(--text-rgb) / 0.25); }
#ui .ck .ck-cs > i.peak { background: rgb(var(--text-rgb) / 0.55); }
#ui .ck .ck-cs > i.dec {
  box-sizing: border-box;
  border: 1px solid rgb(var(--text-rgb) / 0.25);
  background: repeating-linear-gradient(135deg,
    rgb(var(--text-rgb) / 0.35) 0 2px, transparent 2px 5px);
}
#ui .ck .ck-cs > b {
  position: absolute;
  top: -4px;
  width: 2px;
  height: 16px;
  margin-left: -1px;
  background: var(--hero-ink);
  box-shadow: 0 0 0 2px rgb(var(--deep-rgb) / 0.8);
}

/* The backstory folded to one line with MORE; LESS unfolds it whole. */
#ui .ck .ck-story {
  display: flex;
  align-items: baseline;
  gap: var(--s-3);
  margin: var(--s-3) var(--s-4) 0;
  font: var(--t-small);
  color: var(--dim);
}
#ui .ck .ck-story > span { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
#ui .ck .ck-story.open > span { white-space: normal; overflow-wrap: anywhere; color: var(--text); }
#ui .ck .ck-sm {
  flex: none;
  min-height: 0;
  margin: 0;
  padding: 0;
  background: transparent;
  border: 0;
  color: var(--accent);
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
}
@media (pointer: coarse) {
  #ui .ck .ck-sm { min-height: var(--target-finger); padding: 0 var(--s-2); }
}
